import { useState } from 'react';
import { influencer } from '@round/api';
import { Dialog, Button, Checkbox, toast } from '@round/creatorbase-ui';
import { getCreatorName } from 'Modules/Plans/CreatorSearch/components/CreatorExploreTable/helpers';
import { PlatformIcon, platformLabels } from 'Modules/Plans/helpers';
import { getCreatorPoolMemberships, CreatorPoolMembership } from './helpers';
import { useRemovePoolCreators } from './useRemovePoolCreators';
import styles from './RemoveCreatorsModal.module.css';

type Props = {
    isOpen: boolean;
    onClose: () => void;
    poolId: number | null | undefined;
    // The same modal serves the single-row action and the bulk toolbar action, so it always takes a list.
    creators: influencer.CreatorSearchResult[];
    // Fired after a removal so the caller can react (e.g. clear the bulk selection).
    onRemoved?: () => void;
};

export const RemoveCreatorsModal = ({ isOpen, onClose, poolId, creators, onRemoved }: Props) => {
    const { removeAccounts, isRemoving } = useRemovePoolCreators(poolId);

    const memberships = getCreatorPoolMemberships(creators, poolId);
    const allAccountIds = memberships.flatMap((membership) => membership.accounts.map((account) => account.accountId));

    // Every in-pool account is removed by default; the checkboxes only narrow that for dual-account creators.
    const [selectedAccountIds, setSelectedAccountIds] = useState<Set<number>>(() => new Set(allAccountIds));

    // Reset the selection whenever the modal is opened against a different set of creators. Deriving it in
    // render (rather than an effect) avoids painting the new creators against the previous selection.
    const membershipKey = memberships.map((membership) => membership.creator._id).join(',');
    const [selectionKey, setSelectionKey] = useState(membershipKey);
    if (selectionKey !== membershipKey) {
        setSelectionKey(membershipKey);
        setSelectedAccountIds(new Set(allAccountIds));
    }

    const toggleAccount = (accountId: number) => {
        setSelectedAccountIds((previous) => {
            const next = new Set(previous);
            if (next.has(accountId)) {
                next.delete(accountId);
            } else {
                next.add(accountId);
            }

            return next;
        });
    };

    const handleOpenChange = (open: boolean) => {
        // A removal in flight owns the modal until it settles, so it can't be dismissed underneath itself.
        if (!open && !isRemoving) {
            onClose();
        }
    };

    const creatorCount = memberships.length;

    const handleConfirm = async () => {
        const { failedAccountIds } = await removeAccounts(Array.from(selectedAccountIds));
        const label = creatorCount === 1 ? getCreatorName(memberships[0].creator) : `${creatorCount} creators`;

        if (failedAccountIds.length) {
            toast.error(`Could not remove ${label} from the pool`);
        } else {
            toast.success(`Removed ${label} from the pool`);
        }

        // The pool table refetches from the mutation's invalidation, so it reflects whatever landed even
        // on a partial failure; close and let it settle rather than retrying against a stale snapshot.
        onRemoved?.();
        onClose();
    };

    // Only a creator with more than one account in the pool needs a choice of which to remove.
    const dualAccountMemberships = memberships.filter((membership) => membership.accounts.length > 1);
    const hasAccountsToRemove = selectedAccountIds.size > 0;

    return (
        <Dialog open={isOpen} onOpenChange={handleOpenChange}>
            <Dialog.Content>
                <Dialog.Header>
                    <Dialog.Title>{creatorCount === 1 ? 'Remove creator?' : 'Remove creators?'}</Dialog.Title>
                    <Dialog.Description>
                        {creatorCount === 1
                            ? `“${getCreatorName(memberships[0].creator)}” will be removed from this pool.`
                            : `${creatorCount} creators will be removed from this pool.`}
                    </Dialog.Description>
                </Dialog.Header>

                <Dialog.Body className={styles.body}>
                    <p className={styles.note}>Every account a creator has in this pool is removed by default.</p>

                    {dualAccountMemberships.length > 0 ? (
                        <section className={styles.section}>
                            <h3 className={styles.sectionTitle}>Choose which accounts to remove</h3>
                            {dualAccountMemberships.map((membership) => (
                                <CreatorAccounts
                                    key={membership.creator._id}
                                    membership={membership}
                                    // A lone creator's name is already in the description above.
                                    showCreatorName={creatorCount > 1}
                                    selectedAccountIds={selectedAccountIds}
                                    onToggleAccount={toggleAccount}
                                />
                            ))}
                        </section>
                    ) : null}
                </Dialog.Body>

                <Dialog.Footer>
                    <Button variant="outline" size="sm" onClick={onClose} disabled={isRemoving}>
                        Cancel
                    </Button>
                    <Button
                        variant="destructive"
                        size="sm"
                        onClick={handleConfirm}
                        disabled={!hasAccountsToRemove || isRemoving}
                        isLoading={isRemoving}
                    >
                        Remove
                    </Button>
                </Dialog.Footer>
            </Dialog.Content>
        </Dialog>
    );
};

type CreatorAccountsProps = {
    membership: CreatorPoolMembership;
    showCreatorName: boolean;
    selectedAccountIds: Set<number>;
    onToggleAccount: (accountId: number) => void;
};

const CreatorAccounts = ({
    membership,
    showCreatorName,
    selectedAccountIds,
    onToggleAccount,
}: CreatorAccountsProps) => {
    return (
        <div className={styles.creatorGroup}>
            {showCreatorName ? <p className={styles.creatorName}>{getCreatorName(membership.creator)}</p> : null}
            {membership.accounts.map((account) => {
                const checkboxId = `remove-account-${account.accountId}`;

                return (
                    <div key={account.accountId} className={styles.accountRow}>
                        <Checkbox
                            id={checkboxId}
                            checked={selectedAccountIds.has(account.accountId)}
                            onCheckedChange={() => onToggleAccount(account.accountId)}
                        />
                        <label htmlFor={checkboxId} className={styles.accountLabel}>
                            <PlatformIcon platform={account.platform} />
                            {platformLabels[account.platform]}
                            <span className={styles.accountUsername}>@{account.username}</span>
                        </label>
                    </div>
                );
            })}
        </div>
    );
};
